import React, { useState, useEffect, } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Plus, Eye, Edit3, MoreVertical, Play, Pause, Square, Trash2 } from 'lucide-react';
import { Link, useNavigate } from "react-router-dom";
import { selectLogInUsername } from '../../../common/authenticateDashboard/authSlice';
import { deleteTest, updateTestStatus } from '../../../services/mongod.data';
import { fetchAllMetricsapi, selectRealtimeSummary, selectTestMetrics } from '../store/metricsSlice';
import { fetchTests, fetchManuallyDeployedTests, selectTests, selectTestsByStatus, selectTestById, selectStatusCounts, selectActiveTab, selectManuallyDeployedTests, selectLoadingManualTests, setActiveTab } from '../store/abtestSlice';
import { TiArrowBack } from 'react-icons/ti';

const ABTestDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const sessionUser = useSelector(selectLogInUsername);
    const cf_username = sessionUser?.split('-prod@')[0];
    console.log("Username:", sessionUser, cf_username);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [actionLoading, setActionLoading] = useState({});
    const [metricsData, setMetricsData] = useState({});
    const [tests, setTests] = useState([]);
    
    // Redux selectors
    const tests_data = useSelector(selectTests) || [];
    const activeTab = useSelector(selectActiveTab);
    const manuallyDeployedTests = useSelector(selectManuallyDeployedTests);
    const loadingManualTests = useSelector(selectLoadingManualTests);
    const realtimeSummary = useSelector(selectTestMetrics) || {};
    const statusCounts = useSelector(selectStatusCounts) || {};

    // Fetch tests on mount
    useEffect(() => {
        try {
            dispatch(fetchTests({ cf_username }));
            dispatch(fetchAllMetricsapi({ cf_username }));
            dispatch(fetchManuallyDeployedTests({ cf_username }));
        } catch (metricsError) {
            console.warn('⚠️ Metrics loading failed (non-critical):', metricsError);
        }
    }, [dispatch, cf_username]);

    useEffect(() => {
        try {
            setTests(tests_data);
            setMetricsData(realtimeSummary);
        } catch (error) {
            console.error('❌ Error fetching tests:', error);
            setError(error.message);
            setTests([]);
            setMetricsData({});
        } finally {
            setLoading(false);
        }
    }, [tests_data, realtimeSummary]);

    // Handle test status updates
    const handleStatusUpdate = async (testId, newStatus) => {
        setActionLoading(prev => ({ ...prev, [testId]: newStatus }));

        try {
            const result = await updateTestStatus(cf_username, testId, newStatus);

            if (result.success) {
                setTests(prevTests =>
                    prevTests.map(test =>
                        test._id === testId || test.metadata?.testId === testId
                            ? {
                                ...test,
                                metadata: {
                                    ...test.metadata,
                                    status: newStatus,
                                    updatedAt: new Date().toISOString()
                                }
                            }
                            : test
                    )
                );
                console.log(`✅ Test ${testId} status updated to ${newStatus}`);
            } else {
                throw new Error(result.error || 'Failed to update status');
            }
        } catch (err) {
            console.error('❌ Error updating status:', err);
            alert(`Failed to ${newStatus} test: ${err.message}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [testId]: null }));
        }
    };

    // Handle test deletion
    const handleDelete = async (testId, testName) => {
        if (!window.confirm(`Are you sure you want to delete "${testName}"? This action cannot be undone.`)) {
            return;
        }

        setActionLoading(prev => ({ ...prev, [testId]: 'deleting' }));

        try {
            const result = await deleteTest(cf_username, testId);

            if (result.success) {
                setTests(prevTests =>
                    prevTests.filter(test =>
                        test._id !== testId && test.metadata?.testId !== testId
                    )
                );
                console.log(`✅ Test ${testId} deleted successfully`);
            } else {
                throw new Error(result.error || 'Failed to delete test');
            }
        } catch (err) {
            console.error('❌ Error deleting test:', err);
            alert(`Failed to delete test: ${err.message}`);
        } finally {
            setActionLoading(prev => ({ ...prev, [testId]: null }));
        }
    };

    // Filter tests based on active filter
    const filteredTests = tests?.filter(test => {
        if (activeFilter === 'all') return true;
        return test.metadata?.status?.toLowerCase() === activeFilter.toLowerCase();
    });

    // Format date helper
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get test type display name
    const getTestTypeDisplay = (testType) => {
        const types = {
            pricing: 'Pricing',
            discount: 'Discount',
            product_details: 'Product',
            element: 'Element'
        };
        return types[testType?.toLowerCase()] || testType || 'Unknown';
    };

    // Get status badge styles
    const getStatusBadgeStyles = (status) => {
        const styles = {
            active: 'bg-green-100 text-green-800 border border-green-200',
            paused: 'bg-amber-100 text-amber-800 border border-amber-200',
            stopped: 'bg-gray-100 text-gray-700 border border-gray-200',
            completed: 'bg-blue-100 text-blue-800 border border-blue-200',
            draft: 'bg-slate-50 text-slate-600 border border-slate-200'
        };
        return styles[status?.toLowerCase()] || styles.draft;
    };

    // Get type badge styles
    const getTypeBadgeStyles = (type) => {
        const styles = {
            pricing: 'bg-amber-100 text-amber-800 border border-amber-200',
            discount: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
            product: 'bg-violet-100 text-violet-800 border border-violet-200',
            element: 'bg-pink-50 text-pink-800 border border-pink-200'
        };
        return styles[type?.toLowerCase()] || 'bg-slate-100 text-slate-600 border border-slate-200';
    };

    // Render action buttons based on test status
    const renderActionButtons = (test) => {
        const testId = test._id || test.metadata?.testId;
        const status = test.metadata?.status || 'unknown';
        const testName = test.basicInfo?.testName || 'Unnamed Test';
        const isLoading = actionLoading[testId];

        return (
            <div className="flex flex-wrap gap-2 items-center">
                {/* View Button */}
                <Link
                    to={`/abtest-view/${testId}`}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white border border-blue-600 rounded-md hover:bg-blue-700 transition-all duration-200 whitespace-nowrap"
                >
                    <Eye size={14} />
                    View
                </Link>

                {/* Status Control Buttons */}
                {status === 'active' && (
                    <button
                        onClick={() => handleStatusUpdate(testId, 'paused')}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-amber-500 text-white border border-amber-500 rounded-md hover:bg-amber-600 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                        disabled={isLoading}
                    >
                        {isLoading === 'paused' ? (
                            '...'
                        ) : (
                            <>
                                <Pause size={14} />
                                Pause
                            </>
                        )}
                    </button>
                )}

                {status === 'paused' && (
                    <button
                        onClick={() => handleStatusUpdate(testId, 'active')}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-emerald-500 text-white border border-emerald-500 rounded-md hover:bg-emerald-600 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                        disabled={isLoading}
                    >
                        {isLoading === 'active' ? (
                            '...'
                        ) : (
                            <>
                                <Play size={14} />
                                Restart
                            </>
                        )}
                    </button>
                )}

                {/* Stop Button - for active or paused tests */}
                {(status === 'active' || status === 'paused') && (
                    <button
                        onClick={() => handleStatusUpdate(testId, 'stopped')}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-gray-600 text-white border border-gray-600 rounded-md hover:bg-gray-700 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                        disabled={isLoading}
                    >
                        {isLoading === 'stopped' ? (
                            '...'
                        ) : (
                            <>
                                <Square size={14} />
                                Stop
                            </>
                        )}
                    </button>
                )}

                {/* Delete Button */}
                <button
                    onClick={() => handleDelete(testId, testName)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-500 text-white border border-red-500 rounded-md hover:bg-red-600 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    disabled={isLoading}
                >
                    {isLoading === 'deleting' ? (
                        '...'
                    ) : (
                        <>
                            <Trash2 size={14} />
                            Delete
                        </>
                    )}
                </button>
            </div>
        );
    };

    // Show loading state
    if (loading) {
        return (
            <div className="w-full p-4 md:p-6 bg-slate-50 min-h-screen">
                <div className="flex justify-center items-center min-h-[400px] text-slate-500 text-base">
                    <p>Loading tests...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="w-full p-4 md:p-6 bg-slate-50 min-h-screen">
                <div className="text-center py-12 px-6 text-red-600">
                    <p>Error: {error}</p>
                </div>
            </div>
        );
    }


    return (
        <div className="w-full p-4 md:p-6 bg-slate-50 min-h-screen">
            <div className="flex items-center justify-between mb-6">
                <button
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg border 
                  border-gray-500 hover:bg-gray-600 transition-all duration-300 
                  ease-in-out font-semibold text-lg flex justify-center items-center"
                    onClick={() => navigate("/goals")}
                >
                    <TiArrowBack className="text-xl mr-2" />
                    Back
                </button>
            </div>

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 md:mb-8">
                <div className="w-full md:w-auto">
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">
                        A/B Testing Dashboard
                    </h1>
                    <p className="text-slate-600 text-sm md:text-base">
                        Manage and monitor your store's A/B tests to optimize conversions
                    </p>
                </div>
                <Link
                    to="create"
                    className="flex items-center justify-center gap-2 bg-black text-white px-6 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200 text-sm"
                >
                    <Plus size={16} />
                    Create Test
                </Link>
            </div>

            {/* Tests Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-4 md:p-6 border-b border-slate-200">
                    <h2 className="text-lg md:text-xl font-semibold text-slate-800 mb-4">
                        A/B Tests
                    </h2>

                    {/* Tab Switcher for Automated vs Manually Deployed Tests */}
                    <div className="flex gap-2 mb-4 border-b border-slate-200 -mx-4 md:-mx-6 px-4 md:px-6">
                        <button
                            onClick={() => {
                                dispatch(setActiveTab('automated'));
                                setActiveFilter('all');
                            }}
                            className={`px-4 py-3 font-medium text-sm border-b-2 transition-all duration-200 ${
                                activeTab === 'automated'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-slate-600 hover:text-slate-800'
                            }`}
                        >
                            Automated Tests ({tests.length})
                        </button>
                        <button
                            onClick={() => dispatch(setActiveTab('manual'))}
                            className={`px-4 py-3 font-medium text-sm border-b-2 transition-all duration-200 ${
                                activeTab === 'manual'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-slate-600 hover:text-slate-800'
                            }`}
                        >
                            Manually Deployed Tests ({manuallyDeployedTests.length})
                        </button>
                    </div>

                    {/* Filter Tabs - Only show for automated tests */}
                    {activeTab === 'automated' && (
                    <div className="flex flex-wrap gap-1">
                        <button
                            onClick={() => setActiveFilter('all')}
                            className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-lg transition-all duration-200 ${activeFilter === 'all'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            All Tests ({statusCounts.all || 0})
                        </button>
                        <button
                            onClick={() => setActiveFilter('active')}
                            className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-lg transition-all duration-200 ${activeFilter === 'active'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            Active ({statusCounts.active || 0})
                        </button>
                        <button
                            onClick={() => setActiveFilter('paused')}
                            className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-lg transition-all duration-200 ${activeFilter === 'paused'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            Paused ({statusCounts.paused || 0})
                        </button>
                        <button
                            onClick={() => setActiveFilter('stopped')}
                            className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-lg transition-all duration-200 ${activeFilter === 'stopped'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            Stopped ({statusCounts.stopped || 0})
                        </button>
                        <button
                            onClick={() => setActiveFilter('completed')}
                            className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-lg transition-all duration-200 ${activeFilter === 'completed'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            Completed ({statusCounts.completed || 0})
                        </button>
                    </div>
                    )}
                </div>

                {/* AUTOMATED TESTS TABLE SECTION */}
                {activeTab === 'automated' && (
                <div className="overflow-x-auto">
                    {filteredTests.filter(test => test.basicInfo && test._id !== 'store_info').length === 0 ? (
                        <div className="py-12 px-6 text-center text-slate-500 text-sm">
                            <p>No tests found for this filter</p>
                        </div>
                    ) : (
                        <table className="w-full border-collapse">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Test Name
                                    </th>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Type
                                    </th>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Status
                                    </th>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Traffic Split
                                    </th>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Created
                                    </th>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTests
                                    .filter(test => test.basicInfo && test._id !== 'store_info')
                                    .map((test) => {
                                        const testId = test._id || test.metadata?.testId;
                                        const testType = getTestTypeDisplay(test.basicInfo?.testType);
                                        const status = test.metadata?.status || 'unknown';

                                        return (
                                            <tr
                                                key={testId}
                                                className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200"
                                            >
                                                <td className="px-4 md:px-6 py-4 align-top max-w-xs">
                                                    <div className="text-sm font-medium text-slate-800 mb-1">
                                                        {test.basicInfo?.testName || 'Unnamed Test'}
                                                    </div>
                                                    <div className="text-xs text-slate-600 leading-relaxed">
                                                        {test.basicInfo?.hypothesis || test.basicInfo?.description || 'No description'}
                                                    </div>
                                                </td>
                                                <td className="px-4 md:px-6 py-4 align-top">
                                                    <span className={`inline-block px-3 py-1 rounded text-xs font-semibold uppercase tracking-wide ${getTypeBadgeStyles(testType)}`}>
                                                        {testType}
                                                    </span>
                                                </td>
                                                <td className="px-4 md:px-6 py-4 align-top">
                                                    <span className={`inline-block px-3 py-1 rounded text-xs font-semibold uppercase tracking-wide ${getStatusBadgeStyles(status)}`}>
                                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                                    </span>
                                                </td>
                                                <td className="px-4 md:px-6 py-4 align-top">
                                                    <div className="text-sm font-medium text-slate-800">
                                                        {test.settings?.basic?.trafficSplit || 50}%
                                                    </div>
                                                </td>
                                                <td className="px-4 md:px-6 py-4 align-top text-sm text-slate-600">
                                                    {formatDate(test.createdAt || test.metadata?.createdAt)}
                                                </td>
                                                <td className="px-4 md:px-6 py-4 align-top">
                                                    {renderActionButtons(test)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    )}
                </div>
                )}

                {/* MANUALLY DEPLOYED TESTS SECTION */}
                {activeTab === 'manual' && (
                <div className="overflow-x-auto">
                    {loadingManualTests ? (
                        <div className="py-12 px-6 text-center text-slate-500 text-sm">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                            <p>Loading manually deployed tests...</p>
                        </div>
                    ) : manuallyDeployedTests.length === 0 ? (
                        <div className="py-12 px-6 text-center text-slate-500 text-sm">
                            <p>No manually deployed tests found</p>
                        </div>
                    ) : (
                        <table className="w-full border-collapse">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Test Name
                                    </th>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Type
                                    </th>
                                    <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {manuallyDeployedTests.map((testName) => (
                                    <tr
                                        key={testName}
                                        className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200"
                                    >
                                        <td className="px-4 md:px-6 py-4 align-top max-w-xs">
                                            <div className="text-sm font-medium text-slate-800">
                                                {testName}
                                            </div>
                                        </td>
                                        <td className="px-4 md:px-6 py-4 align-top">
                                            <span className="inline-block px-3 py-1 rounded text-xs font-semibold uppercase tracking-wide bg-orange-100 text-orange-800 border border-orange-200">
                                                Manually Deployed
                                            </span>
                                        </td>
                                        <td className="px-4 md:px-6 py-4 align-top">
                                            <div className="flex flex-wrap gap-2 items-center">
                                                <button
                                                    onClick={() => navigate(`/ab-test/${testName}`)}
                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white border border-blue-600 rounded-md hover:bg-blue-700 transition-all duration-200 whitespace-nowrap"
                                                >
                                                    <Eye size={14} />
                                                    View
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                )}
            </div>
        </div>
    );
};

export default ABTestDashboard;